export type CustomerServiceCountryCode = 'MY' | 'ID' | 'VN' | 'TH' | 'SA' | 'AE' | 'HU' | 'DE' | 'BR';

type CustomerServiceLanguageMode = 'zh' | 'en' | 'local';

interface CustomerServiceTextGroup {
  customerServiceText: string;
  serviceTimeText: string;
  hoursTitle: string;
  weekdayText: string;
}

interface CustomerServiceHoursConfig {
  timezone: string;
  localLanguage: string;
  timeRanges: string[];
  texts: Record<CustomerServiceLanguageMode, CustomerServiceTextGroup>;
}

export interface CustomerServiceHoursDisplay {
  countryCode: CustomerServiceCountryCode;
  timezone: string;
  customerServiceText: string;
  serviceTimeText: string;
  hoursTitle: string;
  weekdayText: string;
  timeRanges: string[];
}

const CUSTOMER_SERVICE_LABELS: Record<CustomerServiceLanguageMode, Pick<CustomerServiceTextGroup, 'customerServiceText' | 'serviceTimeText'>> = {
  zh: {
    customerServiceText: '客户服务',
    serviceTimeText: '服务时间',
  },
  en: {
    customerServiceText: 'Customer Service',
    serviceTimeText: 'Service Hours',
  },
  local: {
    customerServiceText: 'Customer Service',
    serviceTimeText: 'Service Hours',
  },
};

const createChineseTexts = (timezone: string): CustomerServiceTextGroup => ({
  ...CUSTOMER_SERVICE_LABELS.zh,
  hoursTitle: '客服时间',
  weekdayText: `周一至周五 (${timezone})`,
});

const createEnglishTexts = (timezone: string): CustomerServiceTextGroup => ({
  ...CUSTOMER_SERVICE_LABELS.en,
  hoursTitle: 'Customer Service Hours',
  weekdayText: `Monday–Friday (${timezone})`,
});

const createLocalTexts = (
  timezone: string,
  text: Pick<CustomerServiceTextGroup, 'customerServiceText' | 'serviceTimeText' | 'hoursTitle' | 'weekdayText'>,
): CustomerServiceTextGroup => ({
  ...text,
  weekdayText: `${text.weekdayText} (${timezone})`,
});

const CUSTOMER_SERVICE_HOURS_CONFIG: Record<CustomerServiceCountryCode, CustomerServiceHoursConfig> = {
  MY: {
    timezone: 'MYT',
    localLanguage: 'ms',
    timeRanges: ['09:00–19:00'],
    texts: {
      zh: createChineseTexts('MYT'),
      en: createEnglishTexts('MYT'),
      local: createLocalTexts('MYT', {
        customerServiceText: 'Khidmat Pelanggan',
        serviceTimeText: 'Waktu Perkhidmatan',
        hoursTitle: 'Waktu Khidmat Pelanggan',
        weekdayText: 'Isnin–Jumaat',
      }),
    },
  },
  ID: {
    timezone: 'WIB',
    localLanguage: 'id',
    timeRanges: ['09:00–19:00'],
    texts: {
      zh: createChineseTexts('WIB'),
      en: createEnglishTexts('WIB'),
      local: createLocalTexts('WIB', {
        customerServiceText: 'Layanan Pelanggan',
        serviceTimeText: 'Jam Layanan',
        hoursTitle: 'Jam Layanan Pelanggan',
        weekdayText: 'Senin–Jumat',
      }),
    },
  },
  VN: {
    timezone: 'ICT',
    localLanguage: 'vi',
    timeRanges: ['09:00–19:00'],
    texts: {
      zh: createChineseTexts('ICT'),
      en: createEnglishTexts('ICT'),
      local: createLocalTexts('ICT', {
        customerServiceText: 'Dịch Vụ Khách Hàng',
        serviceTimeText: 'Giờ Dịch Vụ',
        hoursTitle: 'Giờ Dịch Vụ Khách Hàng',
        weekdayText: 'Thứ Hai–Thứ Sáu',
      }),
    },
  },
  TH: {
    timezone: 'ICT',
    localLanguage: 'th',
    timeRanges: ['09:00–19:00'],
    texts: {
      zh: createChineseTexts('ICT'),
      en: createEnglishTexts('ICT'),
      local: createLocalTexts('ICT', {
        customerServiceText: 'ฝ่ายบริการลูกค้า',
        serviceTimeText: 'เวลาบริการ',
        hoursTitle: 'เวลาทำการฝ่ายบริการลูกค้า',
        weekdayText: 'วันจันทร์–วันศุกร์',
      }),
    },
  },
  SA: {
    timezone: 'AST',
    localLanguage: 'ar',
    timeRanges: ['09:00–19:00'],
    texts: {
      zh: createChineseTexts('AST'),
      en: createEnglishTexts('AST'),
      local: createLocalTexts('AST', {
        customerServiceText: 'خدمة العملاء',
        serviceTimeText: 'ساعات الخدمة',
        hoursTitle: 'ساعات خدمة العملاء',
        weekdayText: 'من الاثنين إلى الجمعة',
      }),
    },
  },
  AE: {
    timezone: 'GST',
    localLanguage: 'ar',
    timeRanges: ['09:00–19:00'],
    texts: {
      zh: createChineseTexts('GST'),
      en: createEnglishTexts('GST'),
      local: createLocalTexts('GST', {
        customerServiceText: 'خدمة العملاء',
        serviceTimeText: 'ساعات الخدمة',
        hoursTitle: 'ساعات خدمة العملاء',
        weekdayText: 'من الاثنين إلى الجمعة',
      }),
    },
  },
  HU: {
    timezone: 'CEST',
    localLanguage: 'hu',
    timeRanges: ['09:00–18:00'],
    texts: {
      zh: createChineseTexts('CEST'),
      en: createEnglishTexts('CEST'),
      local: createLocalTexts('CEST', {
        customerServiceText: 'Ügyfélszolgálat',
        serviceTimeText: 'Ügyfélszolgálati idő',
        hoursTitle: 'Ügyfélszolgálati idő',
        weekdayText: 'Hétfő–péntek',
      }),
    },
  },
  DE: {
    timezone: 'CEST',
    localLanguage: 'de',
    timeRanges: ['09:00–18:00'],
    texts: {
      zh: createChineseTexts('CEST'),
      en: createEnglishTexts('CEST'),
      local: createLocalTexts('CEST', {
        customerServiceText: 'Kundenservice',
        serviceTimeText: 'Servicezeit',
        hoursTitle: 'Kundenservicezeiten',
        weekdayText: 'Montag–Freitag',
      }),
    },
  },
  BR: {
    timezone: 'BRT',
    localLanguage: 'pt-BR',
    timeRanges: ['09:00–13:00', '14:00–18:00'],
    texts: {
      zh: createChineseTexts('BRT'),
      en: createEnglishTexts('BRT'),
      local: createLocalTexts('BRT', {
        customerServiceText: 'Atendimento ao Cliente',
        serviceTimeText: 'Horário de atendimento',
        hoursTitle: 'Horário de Atendimento ao Cliente',
        weekdayText: 'Segunda a sexta-feira',
      }),
    },
  },
};

const LANGUAGE_COUNTRY_FALLBACK: Record<string, CustomerServiceCountryCode> = {
  ms: 'MY',
  id: 'ID',
  vi: 'VN',
  th: 'TH',
  hu: 'HU',
  de: 'DE',
  'pt-BR': 'BR',
};

const normalizeCountryCode = (countryCode?: string | null): CustomerServiceCountryCode | null => {
  const normalizedCountryCode = countryCode?.trim().toUpperCase();
  if (!normalizedCountryCode) {
    return null;
  }

  return normalizedCountryCode in CUSTOMER_SERVICE_HOURS_CONFIG
    ? (normalizedCountryCode as CustomerServiceCountryCode)
    : null;
};

const resolveCountryCode = (countryCode?: string | null, language?: string): CustomerServiceCountryCode | null => {
  return normalizeCountryCode(countryCode) || (language ? LANGUAGE_COUNTRY_FALLBACK[language] || null : null);
};

const resolveLanguageMode = (
  currentLanguage: string | undefined,
  config: CustomerServiceHoursConfig,
): CustomerServiceLanguageMode => {
  if (currentLanguage === 'zh' || currentLanguage === 'zh_Hant') {
    return 'zh';
  }

  if (currentLanguage === config.localLanguage) {
    return 'local';
  }

  return 'en';
};

export const getCustomerServiceHoursDisplay = (
  countryCode?: string | null,
  currentLanguage?: string,
): CustomerServiceHoursDisplay | null => {
  const resolvedCountryCode = resolveCountryCode(countryCode, currentLanguage);
  if (!resolvedCountryCode) {
    return null;
  }

  const config = CUSTOMER_SERVICE_HOURS_CONFIG[resolvedCountryCode];
  const languageMode = resolveLanguageMode(currentLanguage, config);
  const texts = config.texts[languageMode];

  return {
    countryCode: resolvedCountryCode,
    timezone: config.timezone,
    customerServiceText: texts.customerServiceText,
    serviceTimeText: texts.serviceTimeText,
    hoursTitle: texts.hoursTitle,
    weekdayText: texts.weekdayText,
    timeRanges: config.timeRanges,
  };
};
